import React, { useEffect, useRef, useState } from "react";
import { applyLicenseKey } from "../../../../../../utils/License";
import {
  addHierarchyMeta,
  deleteHierarchy,
  getAllHierarchyMeta,
  getParentHierarchy,
  updateHierarchyMeta,
} from "./companyHierarchyService";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import customErrorHandler from "../../../../../../utils/customErrorHandler";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import notifiers from "../../../../../../global/constants/NotificationConstants";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";

interface HierarchyErrors {
  hierarchyName?: string;
  hierarchyLevel?: string;
  parentHierarchy?: string;
}

const LEVEL_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  label: String(i + 1),
  value: String(i + 1),
}));

const ROOT_OPTIONS = [
  { label: "Yes", value: "1" },
  { label: "No", value: "0" },
];

const HierarchyMetaList = () => {
  const [hierarchyData, setHierarchyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState(0); // 0 = Organization, 1 = Team
  const [hierarchyMetaDrawer, setHierarchyMetaDrawer] = useState(false);
  const [hierarchyDrawerData, setHierarchyDrawerData] = useState<any>({});
  const [parentHierarchyList, setParentHierarchyList] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<null | any>(null);
  const [errors, setErrors] = useState<HierarchyErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    applyLicenseKey();
  }, []);

  useEffect(() => {
    handleTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    if (hierarchyDrawerData?.hierarchyLevel) {
      loadParents();
    } else {
      setParentHierarchyList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyDrawerData.hierarchyLevel, hierarchyDrawerData.isOrganization]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const loadParents = async () => {
    try {
      const response = await getParentHierarchy(
        hierarchyDrawerData.hierarchyLevel,
        hierarchyDrawerData.isOrganization || 0
      );
      setParentHierarchyList(
        (response.data || []).map((i: any) => ({
          value: i._id,
          label: i.hierarchyName,
        }))
      );
    } catch {
      setParentHierarchyList([]);
    }
  };

  const handleTableData = async () => {
    try {
      setLoading(true);
      const response = await getAllHierarchyMeta();
      const all = response.data || [];
      const filtered =
        tab === 0
          ? all.filter((item: any) => item.isOrganization === 1)
          : all.filter((item: any) => item.isOrganization === 0);
      setHierarchyData(filtered);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const closeDrawer = () => {
    setHierarchyMetaDrawer(false);
    setHierarchyDrawerData({});
    setErrors({});
  };

  const openCreate = () => {
    setHierarchyDrawerData({
      isOrganization: tab === 0 ? 1 : 0,
      isRootHierarchy: 0,
    });
    setErrors({});
    setHierarchyMetaDrawer(true);
  };

  const handleSubmit = async () => {
    const { hierarchyName, hierarchyLevel, hierarchyParentId } =
      hierarchyDrawerData;
    const newErrors: HierarchyErrors = {};

    if (!hierarchyName) {
      newErrors.hierarchyName = "Hierarchy Name is required";
    }
    if (!hierarchyLevel) {
      newErrors.hierarchyLevel = "Hierarchy Level is required";
    } else if (Number(hierarchyLevel) > 1 && !hierarchyParentId) {
      newErrors.parentHierarchy = "Parent Hierarchy is required";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setSubmitting(true);
      const payload = {
        hierarchyName: hierarchyDrawerData.hierarchyName,
        hierarchyLevel: Number(hierarchyDrawerData.hierarchyLevel),
        hierarchyDescription: hierarchyDrawerData.hierarchyDescription,
        isRootHierarchy: hierarchyDrawerData.isRootHierarchy || 0,
        hierarchyParentId: hierarchyDrawerData.hierarchyParentId || null,
        isOrganization: hierarchyDrawerData.isOrganization || 0,
        id: hierarchyDrawerData._id || null,
      };
      const response = !payload.id
        ? await addHierarchyMeta(payload)
        : await updateHierarchyMeta(payload);
      if (response.success === 1) {
        handleTableData();
        closeDrawer();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow?._id) return;
    try {
      const response = await deleteHierarchy(selectedRow._id);
      customErrorHandler(response);
      openSuccessNotification(response.message);
      if (response.success === 1) {
        handleTableData();
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const filteredRows = hierarchyData.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(row.hierarchyName || "").toLowerCase().includes(q) ||
      String(row.hierarchyDescription || "").toLowerCase().includes(q) ||
      String(row.hierarchyLevel || "").includes(q)
    );
  });

  const columns: YhTableColumn[] = [
    {
      headerName: "Hierarchy Name",
      field: "hierarchyName",
      minWidth: 180,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm font-semibold capitalize text-[#111827]">
          {params.row?.hierarchyName || "—"}
        </span>
      ),
    },
    {
      headerName: "Hierarchy Level",
      field: "hierarchyLevel",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.hierarchyLevel ?? "—"}
        </span>
      ),
    },
    {
      headerName: "Description",
      field: "hierarchyDescription",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="line-clamp-2 text-sm text-[#6B7280]">
          {params.row?.hierarchyDescription || "—"}
        </span>
      ),
    },
    {
      headerName: "Root Hierarchy",
      field: "isRootHierarchy",
      minWidth: 130,
      renderCell: (params: YhTableCellParams) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            params.row?.isRootHierarchy
              ? "bg-[#ECFDF5] text-[#059669]"
              : "bg-[#F3F4F6] text-[#6B7280]"
          }`}
        >
          {params.row?.isRootHierarchy ? "Yes" : "No"}
        </span>
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
            setSelectedRow(params.row);
            setMenuOpen(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      ),
    },
  ];

  const isEdit = !!hierarchyDrawerData._id;
  const levelValue = String(hierarchyDrawerData.hierarchyLevel || "");
  const parentDisabled = !levelValue || levelValue === "1";

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Hierarchy List
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage your hierarchy list here
          </p>
        </div>
        <YhButton variant="primary" size="sm" onClick={openCreate}>
          + Create Hierarchy
        </YhButton>
      </div>

      <div className="mb-3 flex shrink-0 gap-1 rounded-xl border border-[#EEF0F5] bg-white p-1 sm:max-w-md">
        {["Organization", "Team"].map((label, idx) => (
          <button
            key={label}
            type="button"
            onClick={() => setTab(idx)}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
              tab === idx
                ? "bg-[#A02448] text-white shadow-sm"
                : "text-[#6B7280] hover:bg-[#F8FAFC]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search hierarchy…"
          value={search}
          type="search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={filteredRows}
          getRowId={(row) => row._id}
          loading={loading}
          emptyMessage="No hierarchy found"
          className="h-full"
          showPagination={false}
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={() => {
              setHierarchyDrawerData(selectedRow || {});
              setHierarchyMetaDrawer(true);
              setMenuOpen(false);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => {
              setDialogOpen(true);
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {hierarchyMetaDrawer ? (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
          onClick={closeDrawer}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
              <h2 className="text-lg font-bold text-[#111827]">
                {isEdit ? "Edit Hierarchy" : "Create Hierarchy"}
              </h2>
              <button
                type="button"
                onClick={closeDrawer}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="mb-4 flex items-center gap-3">
                <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={hierarchyDrawerData.isOrganization === 1}
                    onChange={(e) => {
                      setHierarchyDrawerData({
                        ...(isEdit ? hierarchyDrawerData : {}),
                        _id: hierarchyDrawerData._id,
                        isOrganization: e.target.checked ? 1 : 0,
                        hierarchyLevel: hierarchyDrawerData.hierarchyLevel,
                        hierarchyName: hierarchyDrawerData.hierarchyName,
                        hierarchyDescription:
                          hierarchyDrawerData.hierarchyDescription,
                        isRootHierarchy: hierarchyDrawerData.isRootHierarchy,
                        hierarchyParentId: "",
                        parentHierarchyName: "",
                      });
                    }}
                  />
                  <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448]" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
                </label>
                <span className="text-sm font-medium text-[#374151]">
                  {hierarchyDrawerData.isOrganization === 1
                    ? "Organization"
                    : "Team"}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <YhInput
                  label="Hierarchy Name"
                  requiredMark
                  placeholder="Hierarchy Name"
                  value={hierarchyDrawerData.hierarchyName ?? ""}
                  error={errors.hierarchyName}
                  onChange={(e) => {
                    setHierarchyDrawerData({
                      ...hierarchyDrawerData,
                      hierarchyName: e.target.value,
                    });
                    if (errors.hierarchyName) {
                      setErrors((prev) => ({ ...prev, hierarchyName: "" }));
                    }
                  }}
                />

                <YhSelect
                  label="Hierarchy Level"
                  requiredMark
                  placeholder="Select"
                  value={levelValue}
                  options={LEVEL_OPTIONS}
                  error={errors.hierarchyLevel}
                  onChange={(e) => {
                    setHierarchyDrawerData({
                      ...hierarchyDrawerData,
                      hierarchyLevel: e.target.value,
                      hierarchyParentId: "",
                      parentHierarchyName: "",
                    });
                    if (errors.hierarchyLevel) {
                      setErrors((prev) => ({ ...prev, hierarchyLevel: "" }));
                    }
                  }}
                />

                <YhSelect
                  label="Parent Hierarchy"
                  placeholder="Select Parent Hierarchy"
                  value={hierarchyDrawerData.hierarchyParentId || ""}
                  options={parentHierarchyList}
                  disabled={parentDisabled}
                  error={errors.parentHierarchy}
                  onChange={(e) => {
                    const selected = parentHierarchyList.find(
                      (p) => String(p.value) === e.target.value
                    );
                    setHierarchyDrawerData({
                      ...hierarchyDrawerData,
                      hierarchyParentId: e.target.value,
                      parentHierarchyName: selected?.label || "",
                    });
                    if (errors.parentHierarchy) {
                      setErrors((prev) => ({ ...prev, parentHierarchy: "" }));
                    }
                  }}
                />

                <YhSelect
                  label="Root Hierarchy"
                  placeholder="Select"
                  value={String(hierarchyDrawerData.isRootHierarchy ?? 0)}
                  options={ROOT_OPTIONS}
                  onChange={(e) => {
                    setHierarchyDrawerData({
                      ...hierarchyDrawerData,
                      isRootHierarchy: Number(e.target.value),
                    });
                  }}
                />

                <div className="sm:col-span-2">
                  <label className="yh-label">Hierarchy Description</label>
                  <textarea
                    className="yh-input min-h-[100px] resize-y"
                    placeholder="Hierarchy Description"
                    value={hierarchyDrawerData.hierarchyDescription ?? ""}
                    onChange={(e) =>
                      setHierarchyDrawerData({
                        ...hierarchyDrawerData,
                        hierarchyDescription: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
              <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
                Cancel
              </YhButton>
              <YhButton
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Submit"}
              </YhButton>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={handleDelete}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default HierarchyMetaList;
